import { useTranslation } from 'react-i18next';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import SupportiveMessage, {
  SupportiveMessageColor,
  SupportiveMessageType,
} from '@lib/components/SupportiveMessage/SupportiveMessage';
import AssistantRow from './AssistantRow';
import { ToolActivityLines } from './ToolActivityRow';
import { AiToolStep } from './ToolStepsStrip';
import { AiErrorAction } from './utils/getAiErrorAction';

type Props = {
  text: string;
  // The remedy offered alongside the message. AiErrorAction.None renders the
  // message on its own — see getAiErrorAction for why some failures get no
  // button at all.
  action?: AiErrorAction;
  // Tools this turn ran before it failed (tool_call_limit_exceeded always has
  // some). They stack above the banner in the same column so the whole turn
  // stays one avatar group.
  toolSteps?: AiToolStep[];
  onStartNewChat?: () => void;
  onUpgradePlan?: () => void;
  onRetry?: () => void;
};

// The message and its remedy, with no lane around it — for the pane-level notice
// that AI is off account-wide, which belongs to no turn and so gets no avatar.
export function AiErrorMessage(props: Omit<Props, 'toolSteps'>) {
  const {
    text,
    action = AiErrorAction.None,
    onStartNewChat,
    onUpgradePlan,
    onRetry,
  } = props;
  const { t } = useTranslation();

  // Each action needs both a mapping and a handler — a caller that cannot honour
  // an action (no onRetry when there is no prompt to re-send, say) renders the
  // message alone rather than a dead button.
  const actionProps = {
    [AiErrorAction.StartNewChat]: {
      label: t('ai-start-new-chat'),
      onClick: onStartNewChat,
    },
    [AiErrorAction.UpgradePlan]: {
      label: t('upgrade-plan'),
      onClick: onUpgradePlan,
    },
    [AiErrorAction.Retry]: { label: t('try-again'), onClick: onRetry },
    [AiErrorAction.None]: { label: '', onClick: undefined },
  }[action];

  return (
    <SupportiveMessage
      fullWidth
      hasBottomSpace={false}
      color={SupportiveMessageColor.Red}
      type={SupportiveMessageType.FilledWithLeftBorder}
      text={text}
      actions={
        actionProps.onClick ? (
          <Button
            buttonText={actionProps.label}
            buttonType={ButtonTypes.negativeFilled}
            buttonSize={ButtonSizes.small}
            onClick={actionProps.onClick}
          />
        ) : undefined
      }
    />
  );
}

// A failed turn's outcome, rendered in the assistant's lane rather than as a
// separate full-width notice: the BE persists it AS an assistant message (a
// blank row carrying status + errorCode — guide §5), so it is the assistant's
// answer to that prompt, and it reads as one. Only the container differs from a
// normal answer — a SupportiveMessage instead of a chat bubble, because the turn
// produced a status rather than something the assistant said.
export default function AiErrorBanner(props: Props) {
  const { toolSteps = [], ...messageProps } = props;

  return (
    <AssistantRow>
      {!!toolSteps.length && <ToolActivityLines toolCalls={toolSteps} />}
      <AiErrorMessage {...messageProps} />
    </AssistantRow>
  );
}
